import axios from 'axios';
import { supabase } from './supabase';
import { BACKEND_URL } from './config';
import { SUPABASE_URL } from '../auth/constants';
import {
  getGuestRequestHeaders,
  persistVisitorFromHeaders,
} from '../utils/guestSearch';
import {
  SEARCH_ERRORS,
  classifySearchError,
  retryAfterSeconds,
} from '../utils/searchErrors';
import { parseSearchEntityHeaders } from '../utils/searchEntity';

export { SEARCH_ERRORS, classifySearchError, retryAfterSeconds };
export { decodeHeaderName, parseSearchEntityHeaders } from '../utils/searchEntity';

function emptyCapabilities(viewer) {
  const allowed = viewer === 'authenticated';
  return {
    canRemind: allowed,
    canBookmark: allowed,
    canTrackHistory: allowed,
  };
}

function headerValue(headers, name) {
  if (!headers) return undefined;
  return headers[name] ?? headers[name.toLowerCase()];
}

function parseViewer(headers, hasToken) {
  const viewer = headerValue(headers, 'x-livenow-viewer');
  if (viewer === 'guest' || viewer === 'authenticated') return viewer;
  return hasToken ? 'authenticated' : 'guest';
}

function parseRemaining(headers) {
  const raw = headerValue(headers, 'x-livenow-remaining-guest-searches');
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function parseFlag(headers, name) {
  return headerValue(headers, name) === '1';
}

function parseCapabilities(headers, viewer) {
  const hasCapabilityHeaders =
    headerValue(headers, 'x-livenow-can-remind') != null
    || headerValue(headers, 'x-livenow-can-bookmark') != null
    || headerValue(headers, 'x-livenow-can-track-history') != null;
  if (!hasCapabilityHeaders) return emptyCapabilities(viewer);
  return {
    canRemind: parseFlag(headers, 'x-livenow-can-remind'),
    canBookmark: parseFlag(headers, 'x-livenow-can-bookmark'),
    canTrackHistory: parseFlag(headers, 'x-livenow-can-track-history'),
  };
}

export function buildSearchResult({ events, headers, hasToken, error, retryAfter }) {
  persistVisitorFromHeaders(headers);
  const viewer = parseViewer(headers, hasToken);
  const entity = parseSearchEntityHeaders(headers);
  return {
    events: Array.isArray(events) ? events : [],
    viewer,
    remainingGuestSearches: parseRemaining(headers),
    capabilities: parseCapabilities(headers, viewer),
    error: error || null,
    retryAfter: retryAfter ?? null,
    entityId: entity.entityId,
    entityName: entity.entityName,
    hubFamily: entity.hubFamily,
  };
}

async function getValidAccessToken() {
  try {
    const projectRef = SUPABASE_URL.split('//')[1].split('.')[0];
    const storageKey = `sb-${projectRef}-auth-token`;
    const storedSession = localStorage.getItem(storageKey);

    if (storedSession) {
      try {
        const parsed = JSON.parse(storedSession);
        if (parsed?.access_token) {
          return parsed.access_token;
        }
      } catch (e) {
      }
    }

    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('getSession timeout')), 5000)
    );

    try {
      const { data: { session }, error: sessionError } = await Promise.race([
        sessionPromise,
        timeoutPromise
      ]);

      if (sessionError) {
        return null;
      }

      if (session?.access_token) {
        return session.access_token;
      }
    } catch (timeoutError) {
    }

    try {
      const refreshPromise = supabase.auth.refreshSession();
      const refreshTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('refreshSession timeout')), 3000)
      );
      const { data, error } = await Promise.race([refreshPromise, refreshTimeout]);

      if (!error && data.session?.access_token) {
        return data.session.access_token;
      }

    } catch (refreshError) {
    }

    return null;
  } catch (error) {
    return null;
  }
}

export const fetchAllEvents = async (query) => {
  const requestUrl = `${BACKEND_URL}/events/${encodeURIComponent(query)}`;
  const token = await getValidAccessToken();

  const doRequest = async (bearer) => {
    const headers = bearer
      ? { Authorization: `Bearer ${bearer}` }
      : getGuestRequestHeaders();
    return axios.get(requestUrl, {
      headers,
      timeout: 30000,
    });
  };

  try {
    let response;
    try {
      response = await doRequest(token);
    } catch (err) {
      const status = err.response?.status;
      if (token && (status === 401 || status === 403)) {
        const refreshed = await supabase.auth.refreshSession();
        const nextToken = refreshed.data?.session?.access_token;
        if (!nextToken) throw err;
        response = await doRequest(nextToken);
      } else {
        throw err;
      }
    }

    const headers = response.headers || {};
    console.log(`Events fetched (${Array.isArray(response.data) ? response.data.length : 0} results)`);
    return buildSearchResult({
      events: response.data,
      headers,
      hasToken: Boolean(token),
      error: null,
    });
  } catch (error) {
    return buildSearchResult({
      events: [],
      headers: error.response?.headers || {},
      hasToken: Boolean(token),
      error: classifySearchError(error),
      retryAfter: retryAfterSeconds(error),
    });
  }
};

export const filterYouTubeEvents = (events) => {
  return (events || []).filter(event => event.platform === 'youtube');
};

export const filterTwitchEvents = (events) => {
  return (events || []).filter(event => event.platform === 'twitch');
};

export const filterKickEvents = (events) => {
  return (events || []).filter(event => event.platform === 'kick');
};

export const filterEPGEvents = (events) => {
  const list = events || [];
  const liveEvents = list.filter(event => event.platform === 'EPG' && event.type === 'Live');
  const upcomingEvents = list.filter(event => event.platform === 'EPG' && event.type === 'Upcoming');

  return {
    liveEvents,
    upcomingEvents,
  };
};
